import { DateTime } from 'luxon';

export type ISO8601String = string;
export const ISO8601Regex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/;

export const daysBeforeNow = (days: number): Date => (
  DateTime.now().minus({ days }).toJSDate()
);

export const daysFromNow = (days: number): Date => (
  DateTime.now().plus({ days }).toJSDate()
);

export const daysFromDate = (date: Date) => {
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date provided');
  }
  const input = DateTime.fromJSDate(date);
  const now = DateTime.now();
  return now.diff(input, 'days').days;
};

export const formatDateOnly = (
  date: Date | string,
  timezone: 'America/New_York' | 'America/Los_Angeles' = 'America/New_York',
  format: string = 'yyyy-MM-dd',
): string => {
  const dateTime = typeof date === 'string'
    ? DateTime.fromISO(date, { zone: timezone })
    : DateTime.fromJSDate(date).setZone(timezone);
  return dateTime.toFormat(format);
};

export const BUSINESS_TIMEZONE = 'America/Los_Angeles';

/**
 * Formats a date in the canonical business timezone. Prefer this over `formatDateOnly` whenever the
 * result feeds a business rule, because `formatDateOnly` defaults to ET, which is already the next
 * calendar day for the last three hours of every Pacific day.
 */
export const formatBusinessDate = (date: Date): string => (
  formatDateOnly(date, BUSINESS_TIMEZONE)
);

/** Today's date in the canonical business timezone, for "is this in the past" style comparisons. */
export const businessToday = (): string => formatBusinessDate(new Date());

/**
 * Formats the calendar day a Date already represents, without converting it to another zone. Use
 * this for date-picker values: the picker hands back local midnight of the day the user clicked, so
 * re-anchoring it to a fixed zone west of the user rolls it back a day. Compare the result against
 * `businessToday()` rather than formatting "now" this way.
 */
export const formatLocalDate = (date: Date): string => (
  DateTime.fromJSDate(date).toFormat('yyyy-MM-dd')
);

export const getCurrentYear = () => new Date().getFullYear().toString();

export const getJsDateFromString = (
  dateOnlyString: string, { endOfDay = false, timezone = 'ET' }: {
    endOfDay?: boolean,
    timezone?: 'ET' | 'PT',
  } = {},
) => {
  const zone = timezone === 'ET' ? 'America/New_York' : 'America/Los_Angeles';
  let dateTime = DateTime.fromISO(dateOnlyString, { zone });
  if (endOfDay) {
    dateTime = dateTime.endOf('day');
  }
  return dateTime.toJSDate();
};

export const getMostRecentDate = (dates: Date[]) => {
  const copy = [...dates];
  const sorted = copy.sort((a, b) => (a === b) ? 0 : (a < b) ? 1 : -1);
  return sorted[0];
};

export const hoursUntil = (date: Date) => {
  const now = new Date();
  const differenceInMilliSeconds = date.getTime() - now.getTime();
  const hours = differenceInMilliSeconds / (60 * 60 * 1000);
  return hours;
};

export const minutesAgo = (minutes: number) => (
  DateTime.now().minus({ minutes }).toJSDate()
);

export const minutesUntil = (date: Date) => {
  return (
    DateTime.fromJSDate(date).diffNow('minutes').minutes
  );
};

export const isWithinMinutes = (date?: Date, minutes: number = 30): boolean => {
  if (!date) return false;

  const now = new Date();
  const thirtyMinutesFromNow = new Date(now.getTime() + minutes * 60000);

  return date <= thirtyMinutesFromNow;
};
